import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import { syncSharedChrome } from '../services/sharedChrome';

/**
 * @param {{ pathname: string, isInternal: boolean, token?: string, isXsPlus: boolean, isAgentAuthenticated: boolean }} params
 * @returns {'none'|'internal'|'external'}
 */
export function resolveChromeMode({
	pathname,
	isInternal,
	token,
	isXsPlus,
	isAgentAuthenticated,
}) {
	if (pathname === '/login') {
		return 'none';
	}

	if (isXsPlus && token) {
		return 'external';
	}

	if (pathname === '/') {
		if (isInternal) {
			return 'internal';
		}
		if (isAgentAuthenticated) {
			return 'internal';
		}
		return 'none';
	}

	return 'internal';
}

export function getChromeSyncParams({
	pathname,
	search = '',
	language,
	isInternal,
	token,
	status,
	forceRefresh = false,
}) {
	const urlParams = queryString.parse(search);
	const urlToken = urlParams?.token;
	const effectiveToken = token || (typeof urlToken === 'string' ? urlToken : '');
	const isXsPlus =
		Boolean(urlToken?.includes('XSPlus')) ||
		Boolean(effectiveToken?.includes('XSPlus'));
	const isAgentAuthenticated = status === true;

	const mode = resolveChromeMode({
		pathname,
		isInternal,
		token: effectiveToken,
		isXsPlus,
		isAgentAuthenticated,
	});

	return {
		pathname,
		language,
		mode,
		token: effectiveToken,
		forceRefresh,
		isInternalUser: isInternal,
	};
}

export function useSharedChrome() {
	const location = useLocation();
	const language = useSelector((state) => state.lang.lang);
	const isInternalUserAutheticated = useSelector(
		(state) => state.internalUser.isInternalUserAutheticated
	);
	const token = useSelector((state) => state.agentProfile.agentToken);
	const status = useSelector((state) => state.agentProfile.isAgentAutheticated);

	useEffect(() => {
		if (window.isGoldenticket?.()) {
			return;
		}

		syncSharedChrome(
			getChromeSyncParams({
				pathname: location.pathname,
				search: location.search,
				language,
				isInternal: isInternalUserAutheticated === true,
				token,
				status,
				forceRefresh: false,
			})
		);
	}, [
		location.pathname,
		location.search,
		token,
		status,
		isInternalUserAutheticated,
		language,
	]);

	const refreshSharedChrome = (nextLanguage = language) => {
		syncSharedChrome(
			getChromeSyncParams({
				pathname: location.pathname,
				search: location.search,
				language: nextLanguage,
				isInternal: isInternalUserAutheticated === true,
				token,
				status,
				forceRefresh: true,
			})
		);
	};

	return { refreshSharedChrome };
}
